/**
 * CreatorDataAdapter - Maps Creator CMS data to internal Data format.
 *
 * Handles two types of artworks:
 * - ImageTarget: Animation content overlaid on a tracked AR marker image
 * - WorldTarget: 3D panel scenes placed in world space (no image tracking)
 */

import type { Data } from "@core/content/types";
import type {
  CreatorArtwork,
  CreatorFile,
  CreatorPayload,
  MicrositeData,
  PanelArtworkEntry,
} from "./types";
import { getArtworkType, getArtworks, isCollection } from "./types";

// =============================================================================
// FILE HELPERS
// =============================================================================

const getFile = (artwork: CreatorArtwork, type: string): CreatorFile | undefined => {
  const requested = type.trim().toLowerCase();
  return artwork.files?.find((f) => f.type?.trim().toLowerCase() === requested);
};

const getRequiredImage = (
  artwork: CreatorArtwork,
  imageType: "artwork1024" | "artwork512"
): CreatorFile => {
  const image = getFile(artwork, imageType) ?? artwork.images?.[imageType];
  if (!image?.path) {
    throw new Error(`Missing ${imageType} for artwork ${artwork.partitionkey}`);
  }
  return image;
};

const getDescription = (artwork: CreatorArtwork): string | undefined => {
  const trimmed = artwork.description?.trim();
  return trimmed || undefined;
};

const getIcon = (artwork: CreatorArtwork): string | undefined => {
  return getRequiredImage(artwork, "artwork512").path;
};

// =============================================================================
// IMAGETARGET MAPPER
// =============================================================================

/**
 * Map an ImageTarget artwork to Data format.
 * ImageTarget = animation video playing over a tracked AR marker image.
 */
const mapImageTargetToData = (artwork: CreatorArtwork): Data | null => {
  const animationFile = getFile(artwork, "animation") ?? artwork.images?.animation;
  if (!animationFile?.path) {
    return null;
  }

  const targetImage = getRequiredImage(artwork, "artwork1024");

  return {
    id: artwork.partitionkey,
    type: "image",
    name: artwork.name ?? artwork.partitionkey,
    description: getDescription(artwork),
    icon: getIcon(artwork),
    content: [
      {
        type: "video",
        videoHasAlpha: artwork.animationHasAlpha ?? true,
        path: animationFile.path,
      },
    ],
    target: {
      targetName: artwork.partitionkey,
      width: targetImage.width,
      height: targetImage.height,
    },
  };
};

// =============================================================================
// WORLDTARGET MAPPER
// =============================================================================

/**
 * Map a WorldTarget artwork to Data format.
 * WorldTarget = 3D panel scene placed in world space (uses sceneConfig for layout).
 * Panels are kept as refs and resolved later when AR content is prepared.
 */
const mapWorldTargetToData = (artwork: CreatorArtwork): Data | null => {
  if (!artwork.sceneConfig) return null;

  const panelEntry: PanelArtworkEntry = {
    id: artwork.id ?? artwork.partitionkey,
    name: artwork.name ?? artwork.partitionkey,
    files: artwork.files as PanelArtworkEntry["files"],
    sceneConfig: artwork.sceneConfig,
  };

  return {
    id: artwork.partitionkey,
    type: "scene",
    name: artwork.name ?? artwork.partitionkey,
    description: getDescription(artwork),
    icon: getIcon(artwork),
    content: [
      {
        type: "panels",
        path: panelEntry,
      },
    ],
  };
};

// =============================================================================
// ARTWORK MAPPER (routes to appropriate mapper by type)
// =============================================================================

/**
 * Map a single artwork to Data format, automatically detecting the artwork type.
 */
export const mapArtworkToData = (artwork: CreatorArtwork): Data | null => {
  const artworkType = getArtworkType(artwork);
  // console.log(`[CreatorMapper] Mapping artwork "${artwork.name ?? artwork.partitionkey}" as ${artworkType}`);

  switch (artworkType) {
    case "worldtarget":
      return mapWorldTargetToData(artwork);
    case "imagetarget":
    default:
      return mapImageTargetToData(artwork);
  }
};

// =============================================================================
// COLLECTION MAPPERS
// =============================================================================

/**
 * Map all artworks from a CreatorPayload to Data format.
 * Handles both collections (multiple artworks) and single artworks.
 */
export const mapCreatorPayloadToContent = (payload: CreatorPayload): Data[] => {
  return getArtworks(payload)
    .map(mapArtworkToData)
    .filter((item): item is Data => item !== null);
};

/**
 * Extract microsite metadata from a CreatorPayload.
 */
export const mapCreatorPayloadToMicrositeData = (payload: CreatorPayload): MicrositeData => {
  const collection = isCollection(payload) ? payload : null;

  return {
    name: collection?.name ?? collection?.title ?? payload.name ?? "",
    description: collection?.description ?? payload.description ?? "",
    constructedCover:
      collection?.constructedCover ?? collection?.cover ?? payload.constructedCover ?? "",
  };
};

